/* ============================================================================
   WooZoo — Waitlist engine (real backend).
   Drives the floating pill + modal and the [data-wl-stage] container, talking
   to the NestJS API through our Next.js /api/waitlist/* route handlers.
   Flow: form → (signup) → "check your inbox". Confirmation happens via the
   emailed link (/confirm), which stores a token; returning visitors with a
   token see their live position via /me.
   ========================================================================== */

import { waitlistApi, type WaitlistStatus } from "@/lib/waitlistApi";

type Locale = "en" | "fr";
const STORAGE = { token: "woozoo.wl.token", pending: "woozoo.wl.pending", ref: "woozoo.wl.ref" };

/* ---------- i18n ---------- */
function lang(): Locale {
  return document.documentElement.lang === "fr" ? "fr" : "en";
}
const T: Record<Locale, Record<string, string>> = {
  en: {
    emailPh: "your@email.com",
    cta: "Reserve my access",
    rgpd: "I agree that WooZoo may use my email to contact me about early access. See the",
    privacy: "Privacy Policy",
    rgpdErr: "Please accept the privacy terms to continue.",
    emailErr: "Please enter a valid email address.",
    genErr: "Something went wrong. Please try again.",
    closed: "Early access is open — the app is live!",
    check: "Check your inbox",
    checkSub: "We sent a confirmation link to",
    checkNote: "Your spot is reserved once you confirm — double opt-in keeps the list clean.",
    resend: "Resend email",
    youAre: "You are",
    number: "No.",
    onList: "on the list",
    totalSignups: "people on the waitlist",
    shareSub: "Each friend who joins with your link and confirms their email moves you up.",
    copy: "Copy",
    copied: "Copied!",
    friends: "friends referred",
    spotsGained: "spots gained",
    inTop: "You're in the top",
    inTopSub: "1 month of Premium, free at launch.",
    launchGoal: "Launch reward",
    goalLead: "Reach the top",
    shareX: "Share on X",
    shareWa: "WhatsApp",
    shareLi: "LinkedIn",
    shareText: "I just reserved my spot on the WooZoo waitlist — know who's really who. Join with my link:",
    pill: "Reserve my access",
    modalTitle: "Reserve your access",
    modalSub: "Join the waitlist and climb with referrals.",
    fullPage: "Open full waitlist page →",
  },
  fr: {
    emailPh: "ton@email.com",
    cta: "Réserver mon accès",
    rgpd: "J'accepte que WooZoo utilise mon email pour me contacter au sujet de l'accès anticipé. Voir la",
    privacy: "Politique de confidentialité",
    rgpdErr: "Merci d'accepter les conditions de confidentialité pour continuer.",
    emailErr: "Merci de saisir une adresse email valide.",
    genErr: "Une erreur est survenue. Réessaie.",
    closed: "L'accès anticipé est ouvert — l'app est disponible !",
    check: "Vérifie ta boîte mail",
    checkSub: "Nous avons envoyé un lien de confirmation à",
    checkNote: "Ta place est réservée une fois confirmée — le double opt-in garde la liste saine.",
    resend: "Renvoyer l'email",
    youAre: "Tu es",
    number: "n°",
    onList: "sur la liste",
    totalSignups: "inscrits sur la liste d'attente",
    shareSub: "Chaque ami qui s'inscrit avec ton lien et confirme son email te fait grimper.",
    copy: "Copier",
    copied: "Copié !",
    friends: "amis parrainés",
    spotsGained: "places gagnées",
    inTop: "Tu es dans le top",
    inTopSub: "1 mois de Premium offert au lancement.",
    launchGoal: "Récompense de lancement",
    goalLead: "Atteins le top",
    shareX: "Partager sur X",
    shareWa: "WhatsApp",
    shareLi: "LinkedIn",
    shareText: "Je viens de réserver ma place sur la liste d'attente WooZoo — savoir qui est vraiment qui. Rejoins-moi avec mon lien :",
    pill: "Réserver mon accès",
    modalTitle: "Réserve ton accès",
    modalSub: "Rejoins la liste et grimpe grâce au parrainage.",
    fullPage: "Ouvrir la page complète →",
  },
};
function t(k: string): string {
  return T[lang()][k] ?? T.en[k] ?? k;
}
function nf(n: number): string {
  return Number(n).toLocaleString(lang() === "fr" ? "fr-FR" : "en-US");
}

/* ---------- storage ---------- */
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, val: string) {
  try {
    localStorage.setItem(key, val);
  } catch {
    /* ignore */
  }
}
function clear(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function captureRef(): string | null {
  try {
    const url = new URL(window.location.href);
    const ref = url.searchParams.get("ref");
    if (ref) write(STORAGE.ref, ref);
  } catch {
    /* ignore */
  }
  return read(STORAGE.ref);
}

/* ---------- counter ---------- */
let enabled = true;
async function refreshCount() {
  try {
    const { enabled: en, count } = await waitlistApi.config();
    enabled = en;
    document.querySelectorAll<HTMLElement>("[data-wl-count]").forEach((el) => {
      el.textContent = nf(count);
    });
  } catch {
    /* ignore */
  }
}

/* ============================ RENDERING ============================ */
function el(html: string): HTMLElement {
  const d = document.createElement("div");
  d.innerHTML = html.trim();
  return d.firstElementChild as HTMLElement;
}

function formHTML(compact?: boolean): string {
  return `
    <form class="wl-form${compact ? " wl-compact" : ""}" novalidate>
      <div class="wl-field">
        <input type="email" class="wl-email" placeholder="${t("emailPh")}" autocomplete="email" required />
        <button type="submit" class="wl-cta">${t("cta")}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
        </button>
      </div>
      <label class="wl-rgpd">
        <input type="checkbox" class="wl-consent" />
        <span>${t("rgpd")} <a href="/legal/privacy" target="_blank" rel="noopener">${t("privacy")}</a>.</span>
      </label>
      <div class="wl-err" hidden></div>
    </form>`;
}

function pendingHTML(email: string): string {
  return `
    <div class="wl-pending">
      <div class="wl-mailicon" aria-hidden="true">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
      </div>
      <h3>${t("check")}</h3>
      <p class="wl-pending-sub">${t("checkSub")}<br/><strong>${email}</strong></p>
      <p class="wl-pending-note">${t("checkNote")}</p>
      <button class="wl-link wl-resend" type="button">${t("resend")}</button>
    </div>`;
}

function closedHTML(): string {
  return `<div class="wl-pending"><h3>${t("closed")}</h3></div>`;
}

function confirmedHTML(s: WaitlistStatus): string {
  const pos = s.position ?? 0;
  const link = s.refLink ?? "";
  const reward = s.inTop
    ? `<div class="wl-reward win">
         <div class="wl-reward-badge">★</div>
         <div><div class="wl-reward-h">${t("inTop")} ${nf(s.rewardTop ?? 1000)} 🎉</div><div class="wl-reward-s">${t("inTopSub")}</div></div>
       </div>`
    : `<div class="wl-reward">
         <div class="wl-reward-top"><span class="mono">${t("launchGoal")}</span></div>
         <div class="wl-reward-lead">${t("goalLead")} ${nf(s.rewardTop ?? 1000)}</div>
       </div>`;
  const enc = encodeURIComponent;
  const shareText = t("shareText") + " " + link;
  return `
    <div class="wl-done">
      <div class="wl-pos">
        <span class="wl-pos-label mono">${t("youAre")}</span>
        <span class="wl-pos-num">${t("number")} ${nf(pos)}</span>
        <span class="wl-pos-sub mono">${t("onList")}</span>
      </div>
      ${reward}
      <div class="wl-meta mono">
        <span><b>${s.confirmedReferrals ?? 0}</b> ${t("friends")}</span>
        <span class="wl-meta-sep"></span>
        <span><b>${nf(s.spotsGained ?? 0)}</b> ${t("spotsGained")}</span>
      </div>
      <div class="wl-share">
        <p class="wl-share-sub">${t("shareSub")}</p>
        <div class="wl-linkbox">
          <input class="wl-reflink" readonly value="${link}" />
          <button class="wl-copy" type="button">${t("copy")}</button>
        </div>
        <div class="wl-share-btns">
          <a class="wl-sbtn" target="_blank" rel="noopener" href="https://twitter.com/intent/tweet?text=${enc(shareText)}">${t("shareX")}</a>
          <a class="wl-sbtn" target="_blank" rel="noopener" href="https://wa.me/?text=${enc(shareText)}">${t("shareWa")}</a>
          <a class="wl-sbtn" target="_blank" rel="noopener" href="https://www.linkedin.com/sharing/share-offsite/?url=${enc(link)}">${t("shareLi")}</a>
        </div>
      </div>
    </div>`;
}

/* ---------- stage controller ---------- */
function renderForm(stage: HTMLElement, compact?: boolean) {
  stage.innerHTML = "";
  stage.appendChild(el(formHTML(compact)));
  const form = stage.querySelector<HTMLFormElement>(".wl-form");
  const err = stage.querySelector<HTMLElement>(".wl-err");
  if (!form || !err) return;
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const email = (form.querySelector<HTMLInputElement>(".wl-email")?.value || "").trim();
    const consent = form.querySelector<HTMLInputElement>(".wl-consent")?.checked || false;
    err.hidden = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      err.textContent = t("emailErr");
      err.hidden = false;
      return;
    }
    if (!consent) {
      err.textContent = t("rgpdErr");
      err.hidden = false;
      return;
    }
    const ref = read(STORAGE.ref) ?? undefined;
    waitlistApi
      .signup(email, ref)
      .then((res) => {
        if (res.status === "closed") {
          stage.innerHTML = "";
          stage.appendChild(el(closedHTML()));
          return;
        }
        write(STORAGE.pending, email);
        renderPending(stage, email);
      })
      .catch((ex: Error) => {
        err.textContent = ex.message || t("genErr");
        err.hidden = false;
      });
  });
}

function renderPending(stage: HTMLElement, email: string) {
  stage.innerHTML = "";
  stage.appendChild(el(pendingHTML(email)));
  stage.querySelector<HTMLElement>(".wl-resend")?.addEventListener("click", (ev) => {
    const b = ev.currentTarget as HTMLElement;
    const ref = read(STORAGE.ref) ?? undefined;
    waitlistApi.signup(email, ref).catch(() => {});
    const o = b.textContent;
    b.textContent = "✓";
    setTimeout(() => {
      b.textContent = o;
    }, 1400);
  });
}

function renderConfirmed(stage: HTMLElement, s: WaitlistStatus) {
  stage.innerHTML = "";
  stage.appendChild(el(confirmedHTML(s)));
  const copyBtn = stage.querySelector<HTMLElement>(".wl-copy");
  copyBtn?.addEventListener("click", () => {
    const input = stage.querySelector<HTMLInputElement>(".wl-reflink");
    if (!input) return;
    input.select();
    if (navigator.clipboard) navigator.clipboard.writeText(input.value).catch(() => {});
    copyBtn.textContent = t("copied");
    copyBtn.classList.add("ok");
    setTimeout(() => {
      copyBtn.textContent = t("copy");
      copyBtn.classList.remove("ok");
    }, 1600);
  });
}

async function driveStage(stage: HTMLElement, compact?: boolean) {
  const token = read(STORAGE.token);
  if (token) {
    try {
      const s = await waitlistApi.me(token);
      if (s.status === "confirmed") {
        renderConfirmed(stage, s);
        return;
      }
    } catch {
      clear(STORAGE.token);
    }
  }
  const pending = read(STORAGE.pending);
  if (pending) {
    renderPending(stage, pending);
    return;
  }
  renderForm(stage, compact);
}

/* ============================ MODAL + PILL ============================ */
function injectModal() {
  if (document.getElementById("wl-modal")) return;
  const modal = el(`
    <div id="wl-modal" class="wl-modal" aria-hidden="true">
      <div class="wl-modal-scrim" data-wl-close></div>
      <div class="wl-modal-card" role="dialog" aria-modal="true" aria-label="Waitlist">
        <button class="wl-modal-x" data-wl-close aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
        </button>
        <div class="wl-modal-head">
          <div class="wl-modal-symbol" aria-hidden="true"></div>
          <h3 class="wl-modal-title">${t("modalTitle")}</h3>
          <p class="wl-modal-sub">${t("modalSub")}</p>
          <div class="wl-modal-counter"><span class="wl-dotlive"></span><strong data-wl-count>—</strong> ${t("totalSignups")}</div>
        </div>
        <div class="wl-modal-stage" data-wl-stage></div>
        <a class="wl-modal-full" href="/waitlist">${t("fullPage")}</a>
      </div>
    </div>`);
  document.body.appendChild(modal);
  const stage = modal.querySelector<HTMLElement>("[data-wl-stage]");
  if (stage) void driveStage(stage, true);
  modal.querySelectorAll<HTMLElement>("[data-wl-close]").forEach((b) => b.addEventListener("click", closeModal));
  void refreshCount();
}
function openModal() {
  injectModal();
  const m = document.getElementById("wl-modal");
  if (!m) return;
  m.classList.add("open");
  m.setAttribute("aria-hidden", "false");
  const inp = m.querySelector<HTMLInputElement>(".wl-email");
  if (inp) setTimeout(() => inp.focus(), 80);
}
function closeModal() {
  const m = document.getElementById("wl-modal");
  if (m) {
    m.classList.remove("open");
    m.setAttribute("aria-hidden", "true");
  }
}

export function openWaitlistModal() {
  openModal();
}

/* ============================ INIT ============================ */
export function initWaitlist(_opts: { pill?: boolean } = {}): () => void {
  captureRef();
  void refreshCount();
  const interval = window.setInterval(refreshCount, 15_000);

  // Drive any full-page stage(s) (skip those inside the modal).
  document.querySelectorAll<HTMLElement>("[data-wl-stage]").forEach((s) => {
    if (!s.closest("#wl-modal")) void driveStage(s, false);
  });

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") closeModal();
  };
  document.addEventListener("keydown", onKey);

  // Re-render injected UI when the page language flips.
  let lastLang = document.documentElement.lang;
  const mo = new MutationObserver(() => {
    if (document.documentElement.lang !== lastLang) {
      lastLang = document.documentElement.lang;
      const m = document.getElementById("wl-modal");
      const wasOpen = m?.classList.contains("open");
      m?.remove();
      document.querySelectorAll<HTMLElement>("[data-wl-stage]").forEach((s) => {
        if (!s.closest("#wl-modal")) void driveStage(s, false);
      });
      if (wasOpen) openModal();
    }
  });
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ["lang"] });

  return () => {
    window.clearInterval(interval);
    mo.disconnect();
    document.removeEventListener("keydown", onKey);
    document.getElementById("wl-modal")?.remove();
  };
}

// Whether the waitlist is currently enabled (read after refreshCount()).
export function isWaitlistEnabled(): boolean {
  return enabled;
}
